// js/sections/vertical-feature-scroller.js
function isView(element, callback, options = {}) {
  const { margin = "0px", amount = 0, root = null, once = false } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isInView = entry.intersectionRatio >= amount;

        if (once && hasTriggered && !isInView) {
          return;
        }

        if (isInView && once) {
          hasTriggered = true;
        }

        callback(isInView, entry);
      });
    },
    {
      root,
      rootMargin: margin,
      threshold: typeof amount === "number" ? [amount] : amount,
    }
  );

  observer.observe(element);

  // 返回清理函数，用于停止观察
  return () => observer.unobserve(element);
}

// 优化的节流函数，使用 requestAnimationFrame
function optimizedThrottle(func, delay = 16) {
  let timeoutId;
  let lastExecTime = 0;
  return function (...args) {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
}

var VerticalFeatureScroller = class extends HTMLElement {
  constructor() {
    super(); // 必须调用 super()
    this.items = [];
    this.currentIndex = 0;
    this._activeItems = new Set(); // 跟踪当前可见的元素
    this._devicePhones = [];
    this._deviceElement = null;
    this._scrollHandler = null; // 用于存储节流后的滚动处理函数
    this._resizeHandler = null; // 用于处理窗口大小变化
    this._isSmallScreen = false; // 标记是否为小屏幕
    this._lastScrollY = window.scrollY; // Track scroll position
    this._isHeaderVisible = true;
    this._product2508Observer = null;
  }

  connectedCallback() {
    // 根据新HTML结构，查找app-feature-main元素
    this.items = Array.from(this.querySelectorAll(".feature-scroller-container .app-feature-main"));

    if (this.items.length === 0) return; // 如果没有子项，则不执行任何操作

    // 查找相关元素
    this._deviceElement = this.querySelector(".featurere-iphone .device");
    this._devicePhones = this.querySelectorAll(".featurere-iphone .app-feature-phone");
    this._appFeatureMains = this.querySelector(".feature-items .app-feature-main");
    this._featureScrollerContainer = this.querySelector(".feature-items .feature-scroller-container");
    this._checkScreenSize();
    // 检查屏幕尺寸并设置响应式行为
    this._setupResizeListener();
    this._setupScrollListener();

    this._setupFadeEffect();

    // 初始状态同步
    this._syncDevicePhones(this.currentIndex);

    // page-dots 点击时派发 control:select，滚动到对应功能项
    this._onControlSelect = (event) => this._scrollToIndex(event.detail.index);
    this.addEventListener("control:select", this._onControlSelect);

    // 设置元素数量CSS变量，用于控制滚动高度
    this.style.setProperty("--items-count", this.items.length);
  }

  disconnectedCallback() {
    // 当元素从 DOM 中移除时，清理事件监听器，防止内存泄漏
    if (this._onControlSelect) {
      this.removeEventListener("control:select", this._onControlSelect);
    }
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    // 如果 View 返回了清理函数，也应该在这里调用它们
    if (this._mobileObserver) {
      this.items.forEach((item) => this._mobileObserver.unobserve(item));
    }
  }

  _setupFadeEffect() {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: Array.from({ length: 101 }, (_, i) => i / 100), // Creates an array [0, 0.01, ..., 1]
    };

    this._mobileObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const item = entry.target;
        const ratio = entry.intersectionRatio;
        const bounds = entry.boundingClientRect;
        const isExitingTop = !entry.isIntersecting && bounds.y < 0;

        if (isExitingTop) {
          // Fading out while leaving the top
          item.style.opacity = "0";
        } else if (entry.isIntersecting) {
          // Fading in from top or bottom
          item.style.opacity = ratio;
        } else {
          // Fully out of view at the bottom, reset
          if (bounds.y > 0) {
            item.style.opacity = "1";
          }
        }
      });
    }, observerOptions);

    this.items.forEach((item) => {
      this._mobileObserver.observe(item);
    });
  }

  /**
   * 检查屏幕尺寸，决定是否使用小屏幕模式，并动态管理Grid类
   */
  _checkScreenSize() {
    const wasSmallScreen = this._isSmallScreen;
    const width = window.innerWidth;
    // 小于1280px启用小屏幕模式
    this._isSmallScreen = width < 1280;

    // 为小屏幕添加特殊类名
    this._applyPCGridClasses();
  }

  /**
   * 应用PC端Grid类
   */
  _applyPCGridClasses() {
    // PC端: featurere 使用 grid-stack

    // PC端: featurere-iphone 和 device 使用 grid-item

    if (this._deviceElement) {
      this._deviceElement.classList.add("grid-item");
      this._deviceElement.classList.add("grid");
    }

    // PC端: feature-item 使用 grid-item

    // PC端: feature-scroller-container 使用 grid-adaptive
    if (this._featureScrollerContainer) {
      this._featureScrollerContainer.classList.remove("grid");
      this._featureScrollerContainer.classList.remove("grid-item");
    }

    // 移除小屏幕模式下的Grid类
    this.items.forEach((item) => {
      item.classList.remove("grid-item");
      item.setAttribute("aria-current", false);
    });
    this._devicePhones.forEach((phone, index) => {
      phone.classList.add("grid-item");
      phone.setAttribute("aria-current", String(index === this.currentIndex));
    });
    this._setupInViewListeners(this.items);
  }

  /**
   * 应用小屏幕Grid类
   */
  _applySmallScreenGridClasses() {
    // 小屏幕: featurere 移除 grid-stack
    if (this._deviceElement) {
      this._deviceElement.classList.remove("grid-item");
      this._deviceElement.classList.remove("grid");
    }

    // PC端: feature-item 使用 grid-item

    // PC端: feature-scroller-container 使用 grid-adaptive
    if (this._featureScrollerContainer) {
      this._featureScrollerContainer.classList.add("grid");
      this._featureScrollerContainer.classList.add("grid-item");
    }
    let height = this.items[0].getBoundingClientRect().height;
    // 移除小屏幕模式下的Grid类
    this.items.forEach((item, index) => {
      item.classList.add("grid-item");
      item.setAttribute("aria-current", String(index === this.currentIndex));
    });
    this._devicePhones.forEach((phone) => {
      phone.classList.remove("grid-item");
      phone.setAttribute("aria-current", false);
      phone.style.height = `${height}px`;
    });

    this._setupInViewListeners(this._devicePhones);
  }

  /**
   * 设置窗口大小变化监听器
   */
  _setupResizeListener() {
    this._resizeHandler = optimizedThrottle(() => {
      this._checkScreenSize();
    }, 100);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  /**
   * 使用 IntersectionObserver 监听每个功能项的进入和离开。
   * 这是一种高效的方式，只在元素进入或离开指定区域时才进行处理。
   */
  getpcisView(item, index) {
    isView(
      item,
      (isInView) => {
        if (isInView) {
          this.currentIndex = index;
          this._handleIndexChange(index);
        }
        return () => {};
      },
      {
        margin: "0px 0px 0px  0px",
        amount: 1,
        once: false,
      }
    );
  }
  getSmallScreenisView(item, index) {
    let title = item.querySelector(".app-feature-text");
    isView(
      title,
      (isInView) => {
        if (isInView) {
          this.currentIndex = index;
          this._handleIndexChange(index);
        }
        return () => {};
      },
      {
        margin: "-20% 0px 0px  0px",
        amount: 1,
        once: false,
      }
    );
  }
  _setupInViewListeners(items) {
    items.forEach((item, index) => {
      if (this._isSmallScreen) {
        this.getSmallScreenisView(item, index);
      } else {
        this.getpcisView(item, index);
      }
    });
  }

  /**
   * 新增：设置滚动监听器，用于在关键位置精确判断当前索引。
   * 这是对 IntersectionObserver 的补充，提供了更精细的控制。
   */
  _setupScrollListener() {
    // 只有当 .device 元素存在时才设置监听
    if (!this._deviceElement) return;

    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  /**
   * 新增：由滚动事件触发，通过计算元素与 .device 元素顶部的距离来更新当前索引。
   * 这是您提出的核心逻辑。
   */
  _updateCurrentIndexByScrolling() {
    if (!this._deviceElement) return;

    // 获取 .device 元素顶部相对于视口的位置
    const triggerPosition = this._isSmallScreen
      ? this._featureScrollerContainer.getBoundingClientRect().top
      : this._deviceElement.getBoundingClientRect().top;

    let bestIndex = -1;
    let minDistance = Infinity;
    let items = this._isSmallScreen ? this.items : this._devicePhones;
    // 遍历所有功能项，找到其中心点离 .device 顶部最近的那一个
    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      // 计算功能项的中心点位置
      const itemCenter = itemRect.top + itemRect.height / 2;
      // 计算功能项中心点与 .device 顶部的垂直距离
      const distance = Math.abs(itemCenter - triggerPosition);

      if (distance < minDistance) {
        minDistance = distance;
        bestIndex = index;
      }
    });
    // 如果找到了最佳索引，并且它与当前索引不同，则更新状态
    if (bestIndex !== -1 && bestIndex !== this.currentIndex) {
      // this.currentIndex = bestIndex;
      this._handleIndexChange(this.currentIndex);
    }
    this._setItemOpacity();
  }
  _setItemOpacity() {
    if (window.innerWidth > 1280) return;
    let index = this.currentIndex;
    let item = this.items[index];
    let item_1 = this.items[index - 1];
    if (!item || !item_1) return;
    const distanceFromTop = item.getBoundingClientRect().top;

    // // 4. 获取视口（窗口）的高度
    const viewportHeight = window.innerHeight;

    if (distanceFromTop > 0) {
      let opacityValue = distanceFromTop / viewportHeight;

      opacityValue = Math.max(0, Math.min(1, opacityValue)); // 确保值在 0 和 1 之间
      // // // // 6. 设置 item_1 的透明度
      item && (item.style.opacity = 1);
      item_1 && (item_1.style.opacity = opacityValue);
      // item_2?.style.opacity = 1 - opacityValue;
    }
    if (index == 0) {
      item.style.opacity = 1;
    }
  }
  /**
   * 当索引发生变化时，触发事件并同步设备预览。
   * @param {number} index - 新的当前索引。
   */
  _handleIndexChange(index) {
    // 触发自定义事件，以便其他组件可以监听变化
    this.dispatchEvent(
      new CustomEvent("scroll:change", {
        detail: { index },
        bubbles: true,
      })
    );
    this.dispatchEvent(
      new CustomEvent("carousel:change", {
        detail: { index },
        bubbles: true,
      })
    );
    // 同步更新设备预览的显示
    if (this._isSmallScreen) {
      this._syncFeatureItems(index);
    } else {
      this._syncDevicePhones(index);
    }
  }

  /**
   * 平滑滚动到指定索引的功能项。
   * @param {number} index - 要滚动到的目标索引。
   */
  _scrollToIndex(index) {
    const targetItem = this.items[index];
    if (!targetItem) return;

    const behavior = window.matchMedia("(prefers-reduced-motion: no-preference)").matches
      ? "smooth"
      : "auto";

    let scrollTop;

    if (this._isSmallScreen) {
      const title = targetItem.querySelector(".app-feature-text") || targetItem;
      const rect = title.getBoundingClientRect();
      scrollTop = window.scrollY + rect.top - window.innerHeight * 0.2;
    } else {
      const rect = targetItem.getBoundingClientRect();
      const triggerOffset = this._deviceElement
        ? this._deviceElement.getBoundingClientRect().top
        : 210;
      scrollTop = window.scrollY + rect.top - triggerOffset;
    }

    window.scrollTo({ top: Math.max(0, scrollTop), behavior });

    this.currentIndex = index;
    this._handleIndexChange(index);
  }
  /**
   * 同步设备预览的状态，只显示与当前索引匹配的预览图。
   * @param {number} index - 当前活动的索引。
   */
  _syncDevicePhones(index) {
    if (!this._devicePhones || this._devicePhones.length === 0) return;
    this._devicePhones.forEach((el, i) => {
      el.setAttribute("aria-current", String(i === index));
    });
  }
  _syncFeatureItems(index) {
    this.items.forEach((el, i) => {
      el.setAttribute("aria-current", String(i === index));
    });
  }

  /**
   * 节流（throttle）辅助函数。确保一个函数在指定的时间间隔内最多只执行一次。
   * @param {Function} func - 需要节流的函数。
   * @param {number} limit - 时间间隔（毫秒）。
   * @returns {Function} - 经过节流处理的新函数。
   */
  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
};

// 注册自定义元素，确保只注册一次
if (!window.customElements.get("vertical-feature-scroller")) {
  window.customElements.define("vertical-feature-scroller", VerticalFeatureScroller);
}
